import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, Sparkles, Trash2, X, Copy, Plus, Building2 } from "lucide-react";

const STATUS_OPTS = ["new", "emailed", "replied", "won", "lost"];
const STATUS_CLS = {
  new: "bg-slate-100 text-slate-600", emailed: "bg-sky-50 text-brand-accent",
  replied: "bg-warning-bg text-warning", won: "bg-success-bg text-success", lost: "bg-danger-bg text-danger",
};

export default function Outreach() {
  const [leads, setLeads] = useState(null);
  const [imp, setImp] = useState({ naics: "541330", start_date: "2024-01-01", end_date: "2025-12-31", pages: 1, page_size: 50 });
  const [importing, setImporting] = useState(false);
  const [manual, setManual] = useState({ company_name: "", naics: "", cage_code: "", contact_email: "" });
  const [emailFor, setEmailFor] = useState(null);
  const [email, setEmail] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  const load = useCallback(() => api.get("/leads").then((r) => setLeads(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/leads/import", imp);
      toast.success(`Imported ${data.imported} leads for NAICS ${data.naics}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const addManual = async () => {
    if (!manual.company_name.trim()) return toast.error("Company name required.");
    try {
      await api.post("/leads/manual", manual);
      setManual({ company_name: "", naics: "", cage_code: "", contact_email: "" });
      toast.success("Lead added.");
      load();
    } catch (e) { toast.error("Failed to add lead"); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    load();
  };

  const removeLead = async (id) => { await api.delete(`/leads/${id}`); load(); };

  const openEmail = async (lead) => {
    setEmailFor(lead);
    if (lead.email_subject && lead.email_body) { setEmail({ subject: lead.email_subject, body: lead.email_body }); return; }
    setEmail(null); setGenBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/email`);
      setEmail(data);
      load();
    } catch (e) { toast.error("Email generation failed"); setEmailFor(null); }
    finally { setGenBusy(false); }
  };

  const regenerate = async () => {
    setGenBusy(true); setEmail(null);
    try {
      const { data } = await api.post(`/leads/${emailFor.id}/email`);
      setEmail(data); load();
    } catch (e) { toast.error("Failed"); }
    finally { setGenBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied."); };

  if (!leads) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-1">Go-To-Market Engine</div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Outreach</h1>
        <p className="text-slate-500 text-sm mt-1">Pull real defense contractors from USASpending.gov and auto-write the CAGE/DFARS cold-email hook.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <ImportPanel imp={imp} setImp={setImp} importing={importing} onImport={runImport} />
        <ManualLeadPanel manual={manual} setManual={setManual} onAdd={addManual} />
      </div>

      <LeadTable leads={leads} onStatus={setStatus} onRemove={removeLead} onEmail={openEmail} />

      {emailFor && (
        <EmailDialog emailFor={emailFor} email={email} genBusy={genBusy} onCopy={copy}
          onClose={() => setEmailFor(null)} onRegenerate={regenerate}
          onMarkEmailed={() => { setStatus(emailFor.id, "emailed"); setEmailFor(null); }} />
      )}
    </div>
  );
}

function ImportPanel({ imp, setImp, importing, onImport }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm lg:col-span-2">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Download className="h-4 w-4 text-brand-accent" /> Import from USASpending.gov</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-slate-600">NAICS prefix</label>
          <input value={imp.naics} onChange={(e) => setImp({ ...imp, naics: e.target.value })} data-testid="import-naics"
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">From</label>
          <input type="date" value={imp.start_date} onChange={(e) => setImp({ ...imp, start_date: e.target.value })}
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">To</label>
          <input type="date" value={imp.end_date} onChange={(e) => setImp({ ...imp, end_date: e.target.value })}
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        </div>
        <button onClick={onImport} disabled={importing} data-testid="import-btn"
          className="bg-brand text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-secondary transition-colors disabled:opacity-50">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Import
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-2">Tip: 541330 = engineering services, 336411 = aircraft mfg, 541512 = computer systems design. NAICS is prefix-matched.</p>
    </div>
  );
}

function ManualLeadPanel({ manual, setManual, onAdd }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-brand-accent" /> Add lead manually</h3>
      <input value={manual.company_name} onChange={(e) => setManual({ ...manual, company_name: e.target.value })} placeholder="Company name" data-testid="manual-company"
        className="w-full mb-2 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
      <div className="grid grid-cols-2 gap-2">
        <input value={manual.cage_code} onChange={(e) => setManual({ ...manual, cage_code: e.target.value })} placeholder="CAGE" className="px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
        <input value={manual.naics} onChange={(e) => setManual({ ...manual, naics: e.target.value })} placeholder="NAICS" className="px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
      </div>
      <input value={manual.contact_email} onChange={(e) => setManual({ ...manual, contact_email: e.target.value })} placeholder="Contact email" className="w-full my-2 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-accent focus:outline-none" />
      <button onClick={onAdd} data-testid="manual-add-btn" className="w-full border border-slate-300 rounded-md py-1.5 text-sm font-medium hover:bg-slate-50">Add lead</button>
    </div>
  );
}

function LeadRow({ l, onStatus, onRemove, onEmail }) {
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`lead-${l.id}`}>
      <td className="px-5 py-3 font-medium">
        <div className="flex items-center gap-2">
          {l.company_name}
          {l.source === "inbound_score" && <span className="text-[10px] font-semibold uppercase tracking-wide bg-success-bg text-success px-1.5 py-0.5 rounded" data-testid={`inbound-${l.id}`}>Inbound</span>}
        </div>
        <div className="text-xs text-slate-400 font-normal">{l.contact_email || l.location || "—"}</div>
      </td>
      <td className="px-3 py-3 font-mono text-xs">{l.cage_code || l.uei || "—"}</td>
      <td className="px-3 py-3 font-mono text-xs">{l.naics || "—"}</td>
      <td className="px-3 py-3 text-right">{l.award_count}</td>
      <td className="px-3 py-3 text-right font-mono text-xs">${Math.round(l.award_total).toLocaleString()}</td>
      <td className="px-3 py-3">
        <select value={l.status} onChange={(e) => onStatus(l.id, e.target.value)} data-testid={`status-${l.id}`}
          className={`text-xs font-semibold rounded px-2 py-1 border-0 cursor-pointer ${STATUS_CLS[l.status]}`}>
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <button onClick={() => onEmail(l)} data-testid={`email-${l.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-accent hover:underline mr-3">
          <Sparkles className="h-3.5 w-3.5" /> {l.email_subject ? "View email" : "AI email"}
        </button>
        <button onClick={() => onRemove(l.id)} className="text-slate-400 hover:text-danger" data-testid={`del-lead-${l.id}`}><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

function LeadTable({ leads, onStatus, onRemove, onEmail }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Lead pipeline ({leads.length})</div>
      {leads.length === 0 ? (
        <div className="p-10 text-center text-slate-500 flex flex-col items-center gap-3"><Building2 className="h-8 w-8 text-slate-300" />No leads yet. Import from USASpending or add one manually.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3">Company</th><th className="px-3 py-3">CAGE / UEI</th><th className="px-3 py-3">NAICS</th>
            <th className="px-3 py-3 text-right">Awards</th><th className="px-3 py-3 text-right">Total</th>
            <th className="px-3 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {leads.map((l) => <LeadRow key={l.id} l={l} onStatus={onStatus} onRemove={onRemove} onEmail={onEmail} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EmailDialog({ emailFor, email, genBusy, onClose, onRegenerate, onMarkEmailed, onCopy }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="email-dialog">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold">Cold email · {emailFor.company_name}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="p-6">
          {genBusy || !email ? (
            <div className="py-10 flex flex-col items-center gap-3 text-slate-500"><Loader2 className="h-6 w-6 animate-spin text-brand-accent" /> Writing personalized email…</div>
          ) : (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1"><div className="overline">Subject</div>
                  <button onClick={() => onCopy(email.subject)} className="text-xs text-brand-accent flex items-center gap-1"><Copy className="h-3 w-3" /> Copy</button></div>
                <div className="text-sm font-semibold border border-slate-200 rounded-md px-3 py-2 bg-slate-50">{email.subject}</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1"><div className="overline">Body</div>
                  <button onClick={() => onCopy(email.body)} className="text-xs text-brand-accent flex items-center gap-1"><Copy className="h-3 w-3" /> Copy</button></div>
                <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed border border-slate-200 rounded-md px-3 py-3 bg-white text-slate-700">{email.body}</pre>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={onRegenerate} data-testid="regen-email" className="border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 flex items-center gap-2"><Sparkles className="h-4 w-4" /> Regenerate</button>
                <button onClick={onMarkEmailed} data-testid="mark-emailed" className="bg-brand text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-brand-secondary">Mark as emailed</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
